import {
	Box,
	Button,
	createStyles,
	Dialog,
	Input,
	makeStyles,
	MenuItem,
	Paper,
	Select,
	Slide,
	Table,
	TableBody,
	TableCell,
	TableRow,
	TextField,
	Theme,
	Typography,
} from '@material-ui/core';
import { ButtonProps } from '@material-ui/core/Button';
import { grey } from '@material-ui/core/colors';
import { InputProps } from '@material-ui/core/Input';
import { SelectProps } from '@material-ui/core/Select';
import { TextFieldProps } from '@material-ui/core/TextField';
import { Buffer } from 'buffer';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
	changeIdentifierInputValue,
	closeSendmailEndDialogById,
	closeSendmailEndDialogByPw,
	IdentifierInputValue,
	selectPwrIdentifier,
	setIdreminderOpen,
	setPwreminderOpen,
	setPwrIdentifierKey,
} from 'src/actions/login';
import { IMessage } from 'src/actions/message';
import {
	DateInputFields,
	IDateInputFieldsValue,
} from 'src/components/DateInputFields';
import MessageDialog from 'src/components/MessageDialog';
import { IRootState } from 'src/reducers';
import i18n from 'src/resources/i18n';
import InputCheck from 'src/utils/InputCheck';
import {
	filterIdentificationItemTypeForIdremind,
	filterIdentificationItemTypeForPwremind,
	PwrIdentificationItemType,
} from 'src/utils/Transfers';
import * as scenario from '../scenario/login';
import { isMobile } from '../utils/MobileCheck';
import PwrSendMailEnd from './PwrSendMailEnd';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		bootstrapInput: {
			'&:focus': {
				borderColor: '#80bdff',
				boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
			},
			backgroundColor: theme.palette.common.white,
			border: '1px solid #ced4da',
			borderRadius: 4,
			fontSize: 16,
			padding: '10px 12px',
			transition: theme.transitions.create([
				'border-color',
				'box-shadow',
			]),
			width: 'calc(100% - 24px)',
		},
		bootstrapRoot: {
			'label + &': {
				marginTop: theme.spacing(3),
			},
			padding: 0,
		},
		careerDialog: {
			width: '80%',
		},
		cellItemName: {
			backgroundColor: grey[300],
			minWidth: '160px',
			textAlign: 'right',
			width: '20%',
		},
		cellItemNameMobile: {
			backgroundColor: grey[300],
			textAlign: 'center',
			width: '100%',
		},
		cellItemValue: {
			textAlign: 'left',
			width: '80%',
		},
		cellItemValueMobile: {
			textAlign: 'left',
			width: '100%',
		},
		container: {
			borderBottom: `1px solid ${theme.palette.primary.main}`,
			marginBottom: theme.spacing(3),
			paddingBottom: theme.spacing(2),
		},
		form: {
			margin: 'auto',
			maxWidth: 700,
		},
		fullWidth: {
			width: '100%',
		},
		root: {
			flexGrow: 1,
			padding: theme.spacing(2),
		},
		table: {
			minWidth: '700px',
		},
		tableClass: {
			minWidth: '1000px',
		},
		title: {
			fontSize: 14,
			marginBottom: 16,
		},
	})
);

/**
 * フォーム切り替え用
 */
export const pwrFormType = {
	/** ログインID通知およびパスワード再発行 */
	LOGIN_ID_NOTIFICATION: 'LOGIN_ID_NOTIFICATION',
	/** パスワード再発行 */
	PASSWORD_REISSUE: 'PASSWORD_REISSUE',
} as const;

/**
 * 画面種別：パスワード再発行／ログインID通知およびパスワード再発行
 */
export type PwrFormType = keyof typeof pwrFormType;

/**
 * パスワードリマインダダイアログ本体のState型
 */
interface IPwrPwReminderState {
	/** 法人コード */
	companyCode?: string;
	/** 画面種別：パスワード再発行／ログインID通知およびパスワード再発行 */
	readonly type: PwrFormType;
	/** 選択可能な検証対象リスト */
	readonly identificationItems: PwrIdentificationItemType[];
	/** 選択中の検証対象 */
	readonly identifierInput: IdentifierInputValue;
	/** FIXME: 「検証対象」に対応した1行目の要素をまとめて呼ぶいい名前が無いか？ */
	readonly primary: string;
	/** メール送信完了画面のopenFlg */
	readonly sendmailEndOpen: boolean;
}

/**
 * パスワードリマインダダイアログ本体のEvent型
 */
interface IPwrPwReminderEvents {
	/** メッセージダイアログを表示 */
	showMessage: (messages: IMessage[], title: string) => void;
}

/**
 * パスワードリマインダダイアログ本体のProps型
 */
// tslint:disable-next-line:no-empty-interface
interface IPwrPwReminderProps
	extends IPwrPwReminderState,
		IPwrPwReminderEvents {}

/**
 * パスワードリマインダダイアログ本体の内部用イベント型
 */
interface IPwrPwReminderPrivateEvents {
	/** 選択可能な検証対象の入力イベント */
	onChangeIdentifier: ReturnType<
		typeof mapDispatchToProps
	>['onChangeIdentifier'];
	/** 選択可能な検証対象リストの選択イベント */
	selectIdentifier: ReturnType<typeof mapDispatchToProps>['selectIdentifier'];
	/** 「送信」ボタンクリックイベント */
	submitPwr: ReturnType<typeof mapDispatchToProps>['submitPwr'];
	/** パスワード再発行またはログインID通知およびパスワード再発行画面をクローズ */
	closeDialog: ReturnType<typeof mapDispatchToProps>['closeDialog'];
	onChangePrimary: ReturnType<typeof mapDispatchToProps>['onChangePrimary'];
	closeSendmailEndDialog: ReturnType<
		typeof mapDispatchToProps
	>['closeSendmailEndDialog'];
}

/**
 * パスワードリマインダダイアログ本体の内部用Props型
 */
// tslint:disable-next-line:no-empty-interface
interface IPwrPwReminderPrivateProps extends IPwrPwReminderPrivateEvents {}

/**
 * 上部メッセージ
 */
const PwrMessage: React.FC<{ type: PwrFormType }> = ({ type }) => {
	// 画面種別毎の上部メッセージの決定
	const [commentLines] = useMemo(
		() => {
			switch (type) {
				case 'PASSWORD_REISSUE':
					return [
						[
							i18n.t('PwrPwReminder_0001'),
							i18n.t('PwrPwReminder_0002'),
						] as string[],
					] as const;
				case 'LOGIN_ID_NOTIFICATION':
					return [
						[
							i18n.t('PwrPwReminder_0003'),
							i18n.t('PwrPwReminder_0004'),
						] as string[],
					] as const;
				default:
					const exhaustive: never = type;
					throw exhaustive;
			}
		},
		[type]
	);

	// 上部メッセージの表示（各行を i18n しつつ <br /> 挿入し結合）
	return (
		<Typography>
			{commentLines
				.map(line => i18n.t(line) as string)
				.reduce(
					(lhs, rhs) => (
						<React.Fragment>
							{lhs}
							<br />
							{rhs}
						</React.Fragment>
					),
					<React.Fragment />
				)}
		</Typography>
	);
};

/**
 * 検証項目の入力欄: 入力形式により表示を分ける
 * @param props
 */
const IdentifierInput: React.FC<{
	onChangeValue: IPwrPwReminderPrivateEvents['onChangeIdentifier'];
	value: IdentifierInputValue;
}> = ({ onChangeValue, value }) => {
	const classes = useStyles();

	/** DateInputFields用: onChangeをつなぐ */
	const onChangeDateInputFields = useCallback(
		(entity: IDateInputFieldsValue) => {
			if (
				value.type === 'PWREMIND_SHOW_BIRTHDAY' ||
				value.type === 'IDREMIND_SHOW_BIRTHDAY'
			) {
				onChangeValue({ type: value.type, entity });
			}
		},
		[onChangeValue, value]
	);

	/** その他TextField用: onChangeをつなぐ */
	const onChangeElse = useCallback<
		Exclude<TextFieldProps['onChange'], undefined>
	>(
		event => {
			if (
				value.type &&
				value.type !== 'PWREMIND_SHOW_BIRTHDAY' &&
				value.type !== 'IDREMIND_SHOW_BIRTHDAY'
			) {
				onChangeValue({ type: value.type, entity: event.target.value });
			}
		},
		[onChangeValue, value]
	);

	return value.type === 'PWREMIND_SHOW_BIRTHDAY' ||
		value.type === 'IDREMIND_SHOW_BIRTHDAY' ? (
		<DateInputFields
			value={value.entity}
			onChange={onChangeDateInputFields}
		/>
	) : value.type ? (
		<Input
			id="commonIdentifier"
			name="commonIdentifier"
			placeholder={i18n.t(value.type ? value.type : '')}
			value={value.entity}
			onChange={onChangeElse}
			disableUnderline={true}
			classes={{
				input: classes.bootstrapInput,
				root: classes.bootstrapRoot,
			}}
		/>
	) : (
		<TextField disabled={true} />
	);
};

/**
 * 画面中部の「ログインID」「E-Mail」領域
 * @param props
 */
const PrimaryValueRow: React.FC<{
	onChangeValue: (value: string) => void;
	type: PwrFormType;
	value: string;
}> = ({ onChangeValue, type, value }) => {
	const classes = useStyles();

	// typeによる分岐が必要な項目
	const [name, label] = useMemo(
		() => {
			switch (type) {
				case 'PASSWORD_REISSUE':
					return ['loginID', i18n.t('PwrPwReminder_0005')] as const;
				case 'LOGIN_ID_NOTIFICATION':
					return ['eMail', i18n.t('PwrPwReminder_0006')] as const;
				default:
					const exhaustive: never = type;
					throw exhaustive;
			}
		},
		[type, onChangeValue]
	);

	const onChange: Exclude<InputProps['onChange'], undefined> = useCallback(
		event => onChangeValue(event.target.value),
		[onChangeValue]
	);

	if (!isMobile()) {
		return (
			<TableRow>
				<TableCell className={classes.cellItemName}>
					<Typography>{i18n.t(label)}</Typography>
				</TableCell>
				<TableCell className={classes.cellItemValue}>
					<Input
						id={name}
						name={name}
						placeholder={i18n.t(label)}
						value={value}
						onChange={onChange}
						autoFocus={true}
						disableUnderline={true}
						classes={{
							input: classes.bootstrapInput,
							root: classes.bootstrapRoot,
						}}
						disabled={false}
					/>
				</TableCell>
			</TableRow>
		);
	} else {
		return (
			<>
				<TableRow>
					<TableCell className={classes.cellItemNameMobile}>
						<Typography>{i18n.t(label)}</Typography>
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className={classes.cellItemValueMobile}>
						<Input
							id={name}
							name={name}
							placeholder={i18n.t(label)}
							value={value}
							onChange={onChange}
							autoFocus={true}
							disableUnderline={true}
							classes={{
								input: classes.bootstrapInput,
								root: classes.bootstrapRoot,
							}}
							disabled={false}
						/>
					</TableCell>
				</TableRow>
			</>
		);
	}
};

/**
 * 画面中部の検証対象リスト領域
 * @param props
 */
const PwrInputTable: React.FC<{
	onChangeIdentifier: IPwrPwReminderPrivateEvents['onChangeIdentifier'];
	onChangePrimary: ReturnType<IPwrPwReminderPrivateEvents['onChangePrimary']>;
	selectIdentifier: IPwrPwReminderPrivateEvents['selectIdentifier'];
	identificationItems: PwrIdentificationItemType[];
	identifierInput: IdentifierInputValue;
	primary: string;
	type: PwrFormType;
}> = ({
	onChangeIdentifier,
	onChangePrimary,
	selectIdentifier,
	identificationItems,
	identifierInput,
	primary,
	type,
}) => {
	const classes = useStyles();

	/**
	 * identificationItemsを選択中のモードに従ってフィルタリング
	 */
	const items = useMemo(
		() => {
			switch (type) {
				case 'PASSWORD_REISSUE':
					return identificationItems.filter(
						filterIdentificationItemTypeForPwremind
					);
				case 'LOGIN_ID_NOTIFICATION':
					// ログインID通知の場合は検証項目からE-Mailを除外
					return identificationItems.filter(
						filterIdentificationItemTypeForIdremind
					);
			}
		},
		[type, identificationItems]
	);

	/**
	 * 2行目の入力欄
	 */
	const SecondaryRow: React.FC<{
		identifierInput: IdentifierInputValue;
	}> = useCallback(
		({ identifierInput: input }) => (
			<TableRow>
				<TableCell className={classes.cellItemName}>
					<PwrIdentifier
						items={items}
						selected={input}
						selectIdentifier={selectIdentifier}
						type={type}
					/>
				</TableCell>
				<TableCell className={classes.cellItemValue}>
					<IdentifierInput
						value={input}
						onChangeValue={onChangeIdentifier}
					/>
				</TableCell>
			</TableRow>
		),
		[
			classes.cellItemName,
			items,
			selectIdentifier,
			type,
			onChangeIdentifier,
		]
	);

	const SecondaryRowMobile: React.FC<{
		identifierInput: IdentifierInputValue;
	}> = useCallback(
		({ identifierInput: input }) => (
			<>
				<TableRow>
					<TableCell className={classes.cellItemNameMobile}>
						<PwrIdentifier
							items={items}
							selected={input}
							selectIdentifier={selectIdentifier}
							type={type}
						/>
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className={classes.cellItemValueMobile}>
						<IdentifierInput
							value={input}
							onChangeValue={onChangeIdentifier}
						/>
					</TableCell>
				</TableRow>
			</>
		),
		[
			classes.cellItemNameMobile,
			items,
			selectIdentifier,
			type,
			onChangeIdentifier,
		]
	);

	if (!isMobile()) {
		return (
			<Table className={classes.table}>
				<TableBody>
					<PrimaryValueRow
						type={type}
						onChangeValue={onChangePrimary}
						value={primary}
					/>
					{// itemsが空なら行まるごと表示しない
					items.length > 0 ? (
						<SecondaryRow identifierInput={identifierInput} />
					) : null}
				</TableBody>
			</Table>
		);
	} else {
		return (
			<Table>
				<TableBody>
					<PrimaryValueRow
						type={type}
						onChangeValue={onChangePrimary}
						value={primary}
					/>
					{// itemsが空なら行まるごと表示しない
					items.length > 0 ? (
						<SecondaryRowMobile identifierInput={identifierInput} />
					) : null}
				</TableBody>
			</Table>
		);
	}
};

/**
 * 画面中部の検証対象リスト用Style
 */
const useStylesForIdentifier = makeStyles((theme: Theme) =>
	createStyles({
		select: {
			minWidth: '20rem',
		},
		selectMobile: {
			width: '10rem !important',
		},
	})
);

/**
 * 画面中部の検証対象リスト
 * @param param0 検証対象一覧
 */
const PwrIdentifier: React.FC<{
	items: PwrIdentificationItemType[];
	selected: IdentifierInputValue;
	selectIdentifier: ReturnType<typeof mapDispatchToProps>['selectIdentifier'];
	type: PwrFormType;
}> = ({ items, selectIdentifier, selected, type }) => {
	const classes = useStylesForIdentifier();

	// イベントメソッドをメモ化
	const onChange = useCallback<Exclude<SelectProps['onChange'], undefined>>(
		(event, child) => {
			selectIdentifier(event.target.value as PwrIdentificationItemType);
		},
		[selectIdentifier]
	);

	return (
		<Select
			value={selected.type}
			onChange={onChange}
			className={isMobile() ? classes.selectMobile : classes.select}
		>
			{items.map(key => (
				<MenuItem key={key} value={key}>
					{i18n.t(key)}
				</MenuItem>
			))}
		</Select>
	);
};

/**
 * 画面下部のボタン領域
 * @param props
 */
const PwrSubmitArea: React.FC<{
	submit: Exclude<ButtonProps['onClick'], undefined>;
	close: ReturnType<IPwrPwReminderPrivateEvents['closeDialog']>;
}> = ({ submit, close }) => {
	return (
		<Box display="flex" justifyContent="flex-end">
			<Box m={1}>
				<Button variant="contained" color="primary" onClick={submit}>
					{i18n.t('PwrPwReminder_0007')}
				</Button>
			</Box>
			<Box m={1}>
				<Button variant="contained" onClick={close}>
					{i18n.t('PwrPwReminder_0008')}
				</Button>
			</Box>
		</Box>
	);
};

/**
 * パスワードリマインダダイアログ内部
 * @param props
 */
const PwrPwReminder: React.FC<
	IPwrPwReminderProps & IPwrPwReminderPrivateProps
> = ({
	companyCode,
	closeDialog: closeDialogOrig,
	onChangeIdentifier,
	onChangePrimary: onChangePrimaryOrig,
	selectIdentifier,
	submitPwr: submitPwrOrig,
	identificationItems,
	identifierInput,
	sendmailEndOpen,
	closeSendmailEndDialog: closeSendmailEndDialogOrig,
	primary,
	type,
}) => {
	const classes = useStyles();
	const [
		/** aaa */
		messageOpen,
		/**  */
		setMessageOpen,
	] = useState(false);
	const [messages, setMessages] = useState<IMessage[]>([]);
	const [messageTitle, setMessageTitle] = useState('');

	/** メッセージダイアログ閉じる用コールバック処理 */
	const closeMessage = useCallback(
		() => {
			setMessageOpen(false);
		},
		[setMessageOpen]
	);

	/** メッセージダイアログ表示用コールバック処理 */
	const showMessage = useCallback(
		(newMessages: IMessage[], newTitle: string) => {
			setMessages(newMessages);
			setMessageTitle(newTitle);
			setMessageOpen(true);
		},
		[setMessages, setMessageTitle, setMessageOpen]
	);

	/** 「送信」ボタン用コールバック処理 */
	const submitPwr = useCallback(
		submitPwrOrig(showMessage, type, companyCode),
		[submitPwrOrig, showMessage]
	);

	/** 「送信」ボタン用コールバック処理 */
	const submit = useCallback<Exclude<ButtonProps['onClick'], undefined>>(
		() => {
			// TextFieldの値もSelectと同じように共有して渡す
			submitPwr({
				identifierInput,
				primary,
			});
		},
		[submitPwr, identifierInput, primary]
	);

	const onChangePrimary = useCallback(onChangePrimaryOrig(type), [
		type,
		onChangePrimaryOrig,
	]);

	const closeDialog = useCallback(closeDialogOrig(type), [
		type,
		closeDialogOrig,
	]);

	const closeSendmailEndDialog = useCallback(
		closeSendmailEndDialogOrig(type),
		[type, closeSendmailEndDialogOrig]
	);

	return (
		<div className={classes.root}>
			<Paper>
				<div className={classes.form}>
					<PwrMessage type={type} />
					<PwrInputTable
						onChangeIdentifier={onChangeIdentifier}
						onChangePrimary={onChangePrimary}
						selectIdentifier={selectIdentifier}
						identificationItems={identificationItems}
						identifierInput={identifierInput}
						primary={primary}
						type={type}
					/>
					<PwrSubmitArea submit={submit} close={closeDialog} />
				</div>
			</Paper>
			<MessageDialog
				open={messageOpen}
				onClickOk={closeMessage}
				onClose={closeMessage}
				contents={messages}
				title={messageTitle}
				showCancelButton={false}
			/>
			<Dialog
				fullScreen={false}
				TransitionComponent={Transition}
				open={sendmailEndOpen}
				onClose={closeSendmailEndDialog}
				fullWidth={true}
				maxWidth={false}
				classes={{
					paperFullWidth: classes.careerDialog,
				}}
			>
				<PwrSendMailEnd
					type={type}
					closeSendMailDialog={closeSendmailEndDialog}
				/>
			</Dialog>
		</div>
	);
};

const mapStateToProps = (state: IRootState) => ({});

/**
 *
 */
export interface IPwrReminderSubmitPayload {
	/**
	 * aaa
	 */
	identifierInput: IdentifierInputValue;
	/**
	 * bbb
	 */
	primary: string;
}

/**
 * 入力チェック結果
 */
type ICheckOnSubmitResult =
	/** 正常時 */
	| {
			/** 正常 */
			type: 'VALID';
	  }
	/** 異常時 */
	| {
			/** 異常 */
			type: 'INVALID';

			/** エラーメッセージ（置換文字含む） */
			messages: IMessage[];

			/** ダイアログタイトル */
			title: string;
	  };

const checkOnSubmit = (type: PwrFormType) => ({
	identifierInput,
	primary,
}: IPwrReminderSubmitPayload): ICheckOnSubmitResult => {
	const title = i18n.t('PwrPwReminder_0009');
	let errId = null;
	let repStr = null;
	let val = null;

	// ログインID/E-Mailが未入力かをチェック
	if (InputCheck.isBlank(primary)) {
		errId = 'LMCM_E_00703';
		repStr = i18n.t('PwrPwReminder_0010');
		if (type === 'LOGIN_ID_NOTIFICATION') {
			repStr = i18n.t('PwrPwReminder_0011');
		}
	} else {
		switch (identifierInput.type) {
			case 'PWREMIND_SHOW_BIRTHDAY':
			case 'IDREMIND_SHOW_BIRTHDAY':
				// 生年月日が未入力かをチェック
				if (
					InputCheck.isBlank(identifierInput.entity.year) ||
					InputCheck.isBlank(identifierInput.entity.month) ||
					InputCheck.isBlank(identifierInput.entity.day)
				) {
					errId = 'LMCM_E_00703';
					repStr = i18n.t('PwrPwReminder_0012');
				} else {
					// 年月日が正しいかをチェック
					val =
						('0000' + identifierInput.entity.year).slice(-4) +
						'/' +
						('00' + identifierInput.entity.month).slice(-2) +
						'/' +
						('00' + identifierInput.entity.day).slice(-2);
					if (
						identifierInput.entity.year.length > 4 ||
						identifierInput.entity.month.length > 2 ||
						identifierInput.entity.day.length > 2 ||
						!InputCheck.validationIsNumber(
							identifierInput.entity.year
						) ||
						!InputCheck.validationIsNumber(
							identifierInput.entity.month
						) ||
						!InputCheck.validationIsNumber(
							identifierInput.entity.day
						) ||
						!InputCheck.validationDate(val)
					) {
						errId = 'LMCM_E_00705';
					}
				}
				break;
			case 'PWREMIND_SHOW_FULLNAME':
			case 'IDREMIND_SHOW_FULLNAME':
				val = identifierInput.entity;
				// 姓＋名（姓と名の間は全角スペース）が未入力かをチェック
				if (InputCheck.isBlank(val)) {
					errId = 'LMCM_E_00703';
					repStr = i18n.t('PwrPwReminder_0013');
					// 姓と名の間に全角スペースが在るかをチェック
				} else if (
					val.split('　').length === 1 ||
					val.split(' ').length === 2
				) {
					errId = 'LMCM_E_00704';
				} else {
					const splFullName = val.split('　');
					// 姓、名、両方入力されているかをチェック
					if (
						splFullName[0].length === 0 ||
						splFullName[1].length === 0
					) {
						errId = 'LMCM_E_00704';
					}
				}
				break;
			case 'PWREMIND_SHOW_EMAIL':
			case 'PWREMIND_SHOW_FAMILYNAME':
			case 'PWREMIND_SHOW_USERATTRIBUTE1':
			case 'PWREMIND_SHOW_USERATTRIBUTE2':
			case 'PWREMIND_SHOW_USERATTRIBUTE3':
			case 'PWREMIND_SHOW_USERATTRIBUTE4':
			case 'IDREMIND_SHOW_FAMILYNAME':
			case 'IDREMIND_SHOW_USERATTRIBUTE1':
			case 'IDREMIND_SHOW_USERATTRIBUTE2':
			case 'IDREMIND_SHOW_USERATTRIBUTE3':
			case 'IDREMIND_SHOW_USERATTRIBUTE4':
				val = identifierInput.entity;
				// プルダウンで選択された値が未入力かをチェック
				if (InputCheck.isBlank(val)) {
					errId = 'LMCM_E_00703';
					switch (identifierInput.type) {
						case 'PWREMIND_SHOW_EMAIL':
							repStr = i18n.t('PwrPwReminder_0014');
							break;
						case 'PWREMIND_SHOW_FAMILYNAME':
						case 'IDREMIND_SHOW_FAMILYNAME':
							repStr = i18n.t('PwrPwReminder_0015');
							break;
						case 'PWREMIND_SHOW_USERATTRIBUTE1':
						case 'IDREMIND_SHOW_USERATTRIBUTE1':
							repStr = i18n.t('PwrPwReminder_0016');
							break;
						case 'PWREMIND_SHOW_USERATTRIBUTE2':
						case 'IDREMIND_SHOW_USERATTRIBUTE2':
							repStr = i18n.t('PwrPwReminder_0017');
							break;
						case 'PWREMIND_SHOW_USERATTRIBUTE3':
						case 'IDREMIND_SHOW_USERATTRIBUTE3':
							repStr = i18n.t('PwrPwReminder_0018');
							break;
						case 'PWREMIND_SHOW_USERATTRIBUTE4':
						case 'IDREMIND_SHOW_USERATTRIBUTE4':
							repStr = i18n.t('PwrPwReminder_0019');
							break;
					}
				}
				break;
			case undefined:
				break;
			default:
				const exhaust: never = identifierInput;
				throw exhaust;
		}
	}

	/** 入力チェック異常：ダイアログ表示あり：置換文字列なし */
	if (errId !== null && repStr === null) {
		return {
			messages: [{ content: errId }],
			title,
			type: 'INVALID',
		};
		/** 入力チェック異常：ダイアログ表示あり：置換文字列あり */
	} else if (errId !== null && repStr !== null) {
		return {
			messages: [{ content: errId, replacements: [repStr] }],
			title,
			type: 'INVALID',
		};
		/** 入力チェック正常：ダイアログ表示なし */
	} else {
		return { type: 'VALID' };
	}
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	closeDialog: (type: PwrFormType) => () => {
		if (type === 'PASSWORD_REISSUE') {
			dispatch(setPwreminderOpen({ pwreminderOpen: false }));
		} else if (type === 'LOGIN_ID_NOTIFICATION') {
			dispatch(setIdreminderOpen({ idreminderOpen: false }));
		}
	},
	closeSendmailEndDialog: (type: PwrFormType) => () => {
		if (type === 'PASSWORD_REISSUE') {
			dispatch(
				closeSendmailEndDialogByPw({
					pwreminderOpen: false,
					sendmailEndOpen: false,
				})
			);
		} else if (type === 'LOGIN_ID_NOTIFICATION') {
			dispatch(
				closeSendmailEndDialogById({
					idreminderOpen: false,
					sendmailEndOpen: false,
				})
			);
		}
	},
	onChangeIdentifier: (value: IdentifierInputValue) => {
		dispatch(changeIdentifierInputValue(value));
	},
	onChangePrimary: (type: PwrFormType) => (value: string) => {
		switch (type) {
			case 'LOGIN_ID_NOTIFICATION':
				// dispatch(新しく定義したアクションクリエータ({ email: value })); // valueをメールアドレスとして扱う
				dispatch(setPwrIdentifierKey({ identifierKey: value }));
				return;
			case 'PASSWORD_REISSUE':
				// dispatch(新しく定義したアクションクリエータ({ loginId: value })); // valueをログインIDとして扱う
				dispatch(setPwrIdentifierKey({ identifierKey: value }));
				return;
			default:
				const exhaustive: never = type;
				throw exhaustive;
		}
	},
	selectIdentifier: (identifyWith: PwrIdentificationItemType) => {
		dispatch(selectPwrIdentifier({ selected: identifyWith }));
	},
	submitPwr: (
		showMessage: IPwrPwReminderEvents['showMessage'],
		type: PwrFormType,
		companyCode?: string
	) => (data: IPwrReminderSubmitPayload) => {
		const result = checkOnSubmit(type)(data);
		if (result.type !== 'VALID') {
			showMessage(result.messages, result.title);
		} else {
			// 画面種別（1=パスワード再発行、2=ログインID通知およびパスワード再発行）
			const kind: string = type;
			// ログインID／E-Mail
			const input1: string = data.primary;
			// プルダウン選択項目
			let selIdx: string = '';
			// プルダウン選択項目に対する値
			let input2: string = '';
			switch (data.identifierInput.type) {
				// 生年月日 選択時
				case 'PWREMIND_SHOW_BIRTHDAY':
				case 'IDREMIND_SHOW_BIRTHDAY':
					selIdx = data.identifierInput.type;
					input2 =
						('0000' + data.identifierInput.entity.year).slice(-4) +
						'/' +
						('00' + data.identifierInput.entity.month).slice(-2) +
						'/' +
						('00' + data.identifierInput.entity.day).slice(-2);
					break;
				// 生年月日 以外選択時
				case 'PWREMIND_SHOW_FULLNAME':
				case 'PWREMIND_SHOW_EMAIL':
				case 'PWREMIND_SHOW_FAMILYNAME':
				case 'PWREMIND_SHOW_USERATTRIBUTE1':
				case 'PWREMIND_SHOW_USERATTRIBUTE2':
				case 'PWREMIND_SHOW_USERATTRIBUTE3':
				case 'PWREMIND_SHOW_USERATTRIBUTE4':
				case 'IDREMIND_SHOW_FULLNAME':
				case 'IDREMIND_SHOW_FAMILYNAME':
				case 'IDREMIND_SHOW_USERATTRIBUTE1':
				case 'IDREMIND_SHOW_USERATTRIBUTE2':
				case 'IDREMIND_SHOW_USERATTRIBUTE3':
				case 'IDREMIND_SHOW_USERATTRIBUTE4':
					selIdx = data.identifierInput.type;
					input2 = data.identifierInput.entity;
					break;
				// その他
				case undefined:
					break;
				default:
					const exhaust: never = data.identifierInput;
					throw exhaust;
			}
			const input1Base64 = Buffer.from(input1, 'utf-8').toString(
				'base64'
			);
			const input2Base64 = Buffer.from(input2, 'utf-8').toString(
				'base64'
			);
			if (kind === 'LOGIN_ID_NOTIFICATION') {
				scenario.pwrSendMail(
					dispatch,
					input1Base64,
					input2Base64,
					'',
					selIdx,
					companyCode
				);
			} else if (kind === 'PASSWORD_REISSUE') {
				scenario.pwrSendMail(
					dispatch,
					'',
					input2Base64,
					input1Base64,
					selIdx,
					companyCode
				);
			}
		}
	},
});

const Transition: React.FC<any> = props => {
	return <Slide direction="up" {...props} />;
};

/**
 * パスワードリマインダダイアログ内本体
 */
export default connect(
	mapStateToProps,
	mapDispatchToProps
)(PwrPwReminder) as React.ComponentType<IPwrPwReminderProps>;



// WEBPACK FOOTER //
// ./src/containers/PwrPwReminder.tsx